import React, { memo, useRef, useMemo, FC } from 'react';
import clsx from 'clsx';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement } from '@marriott/mi-web-ui-kit';
import { Heading, Types, RichText } from '@marriott/mi-ui-library';
import { CobrandImage } from '../CobrandImage/CobrandImage';
import { StyledStickyCardSection } from './StickyCardSection.styles';
import { StickyCardSectionProps, CardImageSectionProps, CardContentSectionProps } from './StickyCardSection.types';
import { extractAnnualFee, replaceWhitespaceAndAddBtn } from '../../modules/utils';
import { usePricingContent } from '../../hooks';
import { CardVariant, ARIA_LABELS, CSS_CLASS_REPLACEMENTS, StringBoolean } from '../../modules/constants';

export const StickyCardSectionConfig = {
  emptyLabel: 'StickyCardSection',
  isEmpty: (): boolean => true,
  resourceType: `/components/content/StickyCardSection`,
} as const;

/**
 * StickyCardSection - Molecule component displaying card information with CTA buttons
 *
 * Performance optimizations:
 * - Memoized component and sub-components
 * - useMemo for computed values
 * - useCallback for event handlers
 * - Extracted sub-components for better maintainability
 */
export const StickyCardSection: FC<StickyCardSectionProps> = memo<StickyCardSectionProps>(props => {
  const {
    componentId,
    imagePath,
    cardTitle,
    altText,
    linkText,
    linkUrl,
    openInaNewTab,
    columnCount,
    hideButtonOnMobile,
    variant = 'horizontal',
    isVerticalImageLayout,
    header,
    pointSectionDescription,
    tertiaryLinks,
    showCtaButton,
    isAmexButton,
    isChaseButton,
    isTertiaryLinksVisible,
    isImagehidden,
    isPointDescriptionVisible,
    isAnnualFeeVisible,
    trackingProperties,
    pricingTermsGuest = '',
    pricingTermsSignedIn = '',
  } = props;
  const ref = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const chaseRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  // Memoized computed values - prevents unnecessary recalculations
  const linkType = useMemo<string>(
    () => (openInaNewTab === true || openInaNewTab === StringBoolean.TRUE ? 'external' : 'internal'),
    [openInaNewTab]
  );

  const displayHeader = useMemo<string>(
    () =>
      variant === CardVariant.VERTICAL
        ? cardTitle
        : cardTitle?.replace(
            CSS_CLASS_REPLACEMENTS.CARD_TITLE_VERTICAL_TO_HORIZONTAL.FROM,
            CSS_CLASS_REPLACEMENTS.CARD_TITLE_VERTICAL_TO_HORIZONTAL.TO
          ) || '',
    [variant, cardTitle]
  );

  const annualFeeText = useMemo<string>(() => extractAnnualFee(header) || '', [header]);

  const buttonId = useMemo<string>(() => replaceWhitespaceAndAddBtn(componentId || ''), [componentId]);

  const hasCtaButton = useMemo<boolean>(
    () => Boolean(isAmexButton || isChaseButton || showCtaButton),
    [isAmexButton, isChaseButton, showCtaButton]
  );

  const isVertical = useMemo<boolean>(() => variant === CardVariant.VERTICAL, [variant]);

  const isSingleColumn = useMemo<boolean>(() => columnCount === '1', [columnCount]);
  const isTwoColumns = useMemo<boolean>(() => columnCount === '2', [columnCount]);

  const pricingContent = usePricingContent(pricingTermsGuest, pricingTermsSignedIn);

  return (
    <StyledStickyCardSection
      className="d-flex justify-content-center"
      data-component-name="m-cobrand-StickyCardSection"
      data-testid="cobrand-StickyCardSection"
    >
      <div
        className={clsx('d-flex sticky-card', {
          'justify-content-center mx-2': isSingleColumn,
          'mr-lg-5 pr-lg-4 mr-0': isTwoColumns,
          'flex-column align-items-center': isVertical,
        })}
      >
        {/* Card Image */}
        {!isImagehidden && (
          <CardImageSection
            imagePath={imagePath}
            altText={altText}
            isVertical={isVertical}
            isVerticalImageLayout={isVerticalImageLayout}
          />
        )}

        {/* Card Content */}
        <CardContentSection
          isVertical={isVertical}
          isSingleColumn={isSingleColumn}
          isTwoColumns={isTwoColumns}
          header={header}
          isPointDescriptionVisible={isPointDescriptionVisible}
          pointSectionDescription={pointSectionDescription}
          displayHeader={displayHeader}
          isAnnualFeeVisible={isAnnualFeeVisible}
          annualFeeText={annualFeeText}
          pricingContent={pricingContent}
          isTertiaryLinksVisible={isTertiaryLinksVisible}
          tertiaryLinks={tertiaryLinks}
          hasCtaButton={hasCtaButton}
          isAmexButton={isAmexButton}
          buttonId={buttonId}
          linkUrl={linkUrl}
          desktopRef={ref}
          trackingProperties={trackingProperties}
          openInaNewTab={openInaNewTab}
          linkText={linkText}
          isChaseButton={isChaseButton}
          showCtaButton={showCtaButton}
          componentId={componentId}
          mobileRef={chaseRef}
          hideButtonOnMobile={hideButtonOnMobile}
        />
      </div>
    </StyledStickyCardSection>
  );
});

StickyCardSection.displayName = 'StickyCardSection';

/**
 * CardImageSection - Separated component for card image
 */
const CardImageSection: FC<CardImageSectionProps> = memo(
  ({ imagePath, altText, isVertical, isVerticalImageLayout }) => (
    <div className="d-flex justify-content-center">
      <CobrandImage
        defaultImageURL={imagePath}
        altText={altText}
        customClass={clsx(
          'sticky-card-image',
          isVertical && 'mt-md-1',
          isVertical && !isVerticalImageLayout && 'sticky-card-image--large',
          isVerticalImageLayout && 'sticky-card-image--vertical'
        )}
      />
    </div>
  )
);

CardImageSection.displayName = 'CardImageSection';

/**
 * CardContentSection - Separated component for card content and CTA buttons
 */
const CardContentSection: FC<CardContentSectionProps> = memo(props => {
  const {
    isVertical,
    isSingleColumn,
    isTwoColumns,
    header,
    isPointDescriptionVisible,
    pointSectionDescription,
    displayHeader,
    isAnnualFeeVisible,
    annualFeeText,
    pricingContent,
    isTertiaryLinksVisible,
    tertiaryLinks,
    hasCtaButton,
    isAmexButton,
    buttonId,
    linkUrl,
    desktopRef,
    trackingProperties,
    openInaNewTab,
    linkText,
    isChaseButton,
    showCtaButton,
    componentId,
    mobileRef,
    hideButtonOnMobile,
  } = props;

  const isOpenInNewTab = openInaNewTab === true || openInaNewTab === StringBoolean.TRUE;

  return (
    <div
      className={clsx('d-flex flex-wrap', {
        'mx-lg-5 mx-md-3 mx-2': isSingleColumn,
        'mx-lg-4 ml-2 mr-md-3': isTwoColumns && !isVertical,
        'mx-lg-1': isVertical && !isSingleColumn && !isTwoColumns,
        'mx-lg-3 ml-2 mr-md-3': !isSingleColumn && !isTwoColumns && !isVertical,
        'justify-content-center text-center': isVertical,
        'flex-column align-items-baseline': !isVertical,
      })}
    >
      {/* Mobile Header for vertical variant */}
      {isVertical && header && (
        <Heading
          titleText={header}
          variation={Types.headingType.title}
          fontSize={Types.size.medium}
          customClass="d-inline d-md-none text-center mt-4"
        />
      )}

      {/* Main card content */}
      <div
        dangerouslySetInnerHTML={{
          __html: isPointDescriptionVisible ? pointSectionDescription || '' : displayHeader,
        }}
        className={clsx({
          'my-3 mb-md-2 mx-2 mx-lg-0 col-12': isVertical,
          'mt-0 mt-lg-2 sticky-card-heading': !isVertical,
          'mb-2': isSingleColumn || isTwoColumns,
          'mb-md-3': !isSingleColumn && !isTwoColumns,
          'no-border-top-bottom flex-grow-1': isPointDescriptionVisible,
        })}
      />

      {/* Annual Fee */}
      {isAnnualFeeVisible && annualFeeText && (
        <RichText text={annualFeeText} componentId="annual-fee-message" customClass="pt-2" />
      )}

      {/* Pricing Terms */}
      {pricingContent && (
        <RichText text={pricingContent} componentId="cardDetails-pricing-text" customClass="mb-md-2" />
      )}

      {/* Tertiary Links */}
      {isTertiaryLinksVisible && tertiaryLinks && (
        <div
          dangerouslySetInnerHTML={{ __html: tertiaryLinks }}
          className={clsx({
            'my-3 mb-md-2 mx-2 mx-lg-0 col-12': isVertical,
            'mt-0 mt-3 sticky-card-links': !isVertical,
            'mb-2': isSingleColumn || isTwoColumns,
            'mb-md-3': !isSingleColumn && !isTwoColumns,
          })}
        />
      )}

      {/* Desktop CTA Button */}
      {hasCtaButton && (
        <div className={clsx({ 'mt-auto': isAmexButton })}>
          <MButton
            id={buttonId}
            ctaLink={linkUrl}
            ref={desktopRef as React.Ref<HTMLButtonElement>}
            trackingProperties={trackingProperties}
            openInANewTab={isOpenInNewTab}
            openInANewTabText={isOpenInNewTab ? ARIA_LABELS.OPEN_NEW_TAB : undefined}
            iconPlacement={isOpenInNewTab ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
            buttonType={MButtonType.Primary}
            size={isAmexButton ? MButtonSize.S : MButtonSize.M}
            className={clsx('d-md-flex', {
              'mt-3': isAmexButton,
              'd-none mt-auto': !isAmexButton,
            })}
            buttonText={linkText}
          />
        </div>
      )}

      {/* Mobile CTA Button */}
      {(isChaseButton || showCtaButton) && (
        <MButton
          id={`${componentId}-btn`}
          ctaLink={linkUrl}
          ref={mobileRef as React.Ref<HTMLButtonElement>}
          trackingProperties={trackingProperties}
          openInANewTab={isOpenInNewTab}
          openInANewTabText={isOpenInNewTab ? ARIA_LABELS.OPEN_NEW_TAB : undefined}
          iconPlacement={isOpenInNewTab ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
          buttonType={MButtonType.Tertiary}
          className={clsx('m-link-tertiary-button-external m-link-action pb-1 mb-1 mt-1 mt-auto mr-0', {
            'd-none': hideButtonOnMobile,
            'd-inline-flex d-md-none': !hideButtonOnMobile,
          })}
          buttonText={linkText}
        />
      )}
    </div>
  );
});

CardContentSection.displayName = 'CardContentSection';
